import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { TokenDto } from './auth.dto';
import { UserDto } from './user.dto';
import { SSO_REDIRECTS_KEY } from '../_helpers/sso-flow';
import { STORAGE_VERSION, STORAGE_VERSION_KEY } from '../_helpers/storage-version';

const USER_KEY = 'auth-user';
const DATA_KEY = 'data-user';
const CLI_SEC = 'client-security';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private userSubject = new BehaviorSubject<UserDto | null>(null);

  /**
   * Utilisateur connecté, ou null tant qu'il n'est pas identifié.
   *
   * Le sujet est réémis à chaque écriture et au nettoyage : les écrans s'y
   * abonnent au lieu d'interroger le stockage en boucle.
   */
  user$ = this.userSubject.asObservable();

  constructor() {
    const storedUser = window.localStorage.getItem(DATA_KEY);
    if (storedUser) {
      this.userSubject.next(JSON.parse(storedUser));
    }
  }

  /**
   * Vrai dès qu'une déconnexion volontaire est lancée. La page n'est pas encore quittée :
   * les requêtes encore en vol reviennent en 401 et ne doivent pas détourner la sortie vers /login.
   */
  private signingOut = false;

  markSigningOut(): void {
    this.signingOut = true;
  }

  isSigningOut(): boolean {
    return this.signingOut;
  }

  clean(): void {
    // Le compteur anti-boucle SSO doit survivre : c'est ce nettoyage qui renvoie vers Keycloak
    const ssoRedirects = window.sessionStorage.getItem(SSO_REDIRECTS_KEY);
    window.sessionStorage.clear();
    if (ssoRedirects) {
      window.sessionStorage.setItem(SSO_REDIRECTS_KEY, ssoRedirects);
    }
    window.localStorage.clear();
    // Sans ce marqueur, le prochain démarrage prendrait le stockage pour celui d'une ancienne version
    window.localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
    this.userSubject.next(null);
  }

  public saveToken(token: TokenDto): void {
    window.localStorage.removeItem(USER_KEY);
    window.localStorage.setItem(USER_KEY, JSON.stringify(token));
    window.sessionStorage.setItem(CLI_SEC, token['client_security'])
  }

  public saveAfterRefresh(token: TokenDto): void {
    window.localStorage.removeItem(USER_KEY);
    window.localStorage.setItem(USER_KEY, JSON.stringify(token));
  }


  public saveInfoUser(user: UserDto): void {
    window.localStorage.removeItem(DATA_KEY);
    window.localStorage.setItem(DATA_KEY, JSON.stringify(user));
    this.userSubject.next(user);
  }

  public getUser(): UserDto | {} {
    const user = window.sessionStorage.getItem(USER_KEY);
    if (user) {
      return JSON.parse(user);
    }

    return {};
  }

  public getToken() {
    const user = window.localStorage.getItem(USER_KEY);
    if (user) {
      return JSON.parse(user)["access_token"];
    }
  }

  public getRefreshToken() {
    const user = window.localStorage.getItem(USER_KEY);
    if (user) {
      return JSON.parse(user)["refresh_token"];
    }
  }

  public getclientSec(): any {
    const clientSecurity = window.sessionStorage.getItem(CLI_SEC);
    if (clientSecurity) {
      return clientSecurity;
    }

    return null;
  }

  public getDataUser(): any {
    const user = window.localStorage.getItem(DATA_KEY);
    if (user) {
      return JSON.parse(user);
    }

    return null;
  }

  public getDataUserObs(): Observable<UserDto | null> {
    const user = window.localStorage.getItem(DATA_KEY);
    if (user) {
      return of(JSON.parse(user));
    }

    return of(null);
  }

  public isLoggedIn(): boolean {
    const user = window.localStorage.getItem(USER_KEY);
    if (user) {
      return true;
    } else {
      return false;
    }
  }

  public isAdmin(): boolean {
    const user = this.getDataUser();
    if (user && user.level > 50) {
      return true;
    }
    return false;

  }
}
